import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BET_LABEL, RISK_COLOR, SPORT_META, formatOdds } from "@/lib/picks";
import { fetchPickAccessForPick } from "@/lib/access";
import { Button } from "@/components/ui/button";
import Disclaimer from "@/components/Disclaimer";
import UpgradeModal from "@/components/UpgradeModal";
import { ArrowLeft, AlertTriangle, TrendingUp, Activity, Lock, Sparkles, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import EdgeExplainer, { deriveSignalGrade } from "@/components/EdgeExplainer";

const RESULT_META: Record<string, { label: string; color: string; Icon: any }> = {
  won: { label: "WON", color: "border-success/40 bg-success/15 text-success", Icon: CheckCircle2 },
  lost: { label: "LOST", color: "border-danger/40 bg-danger/15 text-danger", Icon: XCircle },
  pushed: { label: "PUSH", color: "border-muted-foreground/40 bg-muted/20 text-muted-foreground", Icon: MinusCircle },
};

export default function PickDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [pick, setPick] = useState<any>(null);
  const [breakdown, setBreakdown] = useState<any>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (!id || !user) return;
    (async () => {
      const [{ data: p }, { data: b }, access] = await Promise.all([
        supabase.from("picks").select("*, sports(code)").eq("id", id).is("deleted_at", null).maybeSingle(),
        supabase.from("pick_score_breakdowns").select("*").eq("pick_id", id).maybeSingle(),
        fetchPickAccessForPick(user.id, id),
      ]);
      setPick(p ? { ...p, access } : null); setBreakdown(b);
    })();
  }, [id, user?.id]);

  if (!pick) return <div className="container py-20 text-muted-foreground">Loading…</div>;
  const locked = pick.access?.is_locked ?? false;
  const meta = SPORT_META[pick.sports?.code] ?? SPORT_META.NBA;

  if (locked) {
    const settled = pick.result && pick.result !== "pending" ? RESULT_META[pick.result] : null;
    const unitsDelta = Number(pick.units_delta ?? 0);
    return (
      <div className="container py-10 max-w-3xl">
        <Link to="/top-5"><Button variant="ghost" size="sm" className="mb-6"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
        <div className="te-card p-7 text-center">
          <div className="flex justify-center gap-2 flex-wrap">
            <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
            {settled ? (
              <span className={`te-chip border font-bold tracking-wider ${settled.color}`}>
                <settled.Icon className="w-3.5 h-3.5 mr-1 inline" /> {settled.label}
              </span>
            ) : (
              <span className="te-chip border border-accent/40 bg-accent/10 text-accent">
                <Lock className="w-3 h-3 mr-1 inline" /> Pro
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-bold mt-4">{pick.teams || "Featured Matchup"}</h1>

          {settled && (
            <div className="grid grid-cols-3 gap-3 md:gap-4 mt-6 text-left">
              <Stat label="Odds" value={formatOdds(pick.odds)} />
              <Stat label="Units" value={`${pick.unit_rating}u`} />
              <Stat
                label="Result"
                value={`${unitsDelta >= 0 ? "+" : ""}${unitsDelta.toFixed(2)}u`}
                tone={unitsDelta > 0 ? "success" : unitsDelta < 0 ? "danger" : undefined}
              />
            </div>
          )}

          <div className="mx-auto mt-6 grid place-items-center w-16 h-16 rounded-2xl bg-gradient-accent text-accent-foreground shadow-glow">
            <Lock className="w-7 h-7" />
          </div>
          <p className="mt-4 text-muted-foreground max-w-md mx-auto">
            {settled
              ? "Final outcome shown above. Premium reasoning, score breakdown, and AI signals stay locked for Pro members."
              : "This pick's full breakdown, score signals, and reasoning are unlocked with Pro."}
          </p>
          <Button onClick={() => setUpgradeOpen(true)} className="mt-6 bg-gradient-accent text-accent-foreground">
            <Sparkles className="w-4 h-4 mr-2" /> Unlock today's AI analysis
          </Button>
        </div>
        <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
        <div className="mt-8"><Disclaimer /></div>
      </div>
    );
  }

  const signals = breakdown ? [
    { label: "Market Edge", val: breakdown.line_value ?? breakdown.market_edge_score ?? 0, max: 30 },
    { label: "Line Movement", val: breakdown.market_movement ?? breakdown.line_movement_score ?? 0, max: 15 },
    { label: "Injury/Lineup", val: breakdown.injury_impact ?? breakdown.injury_lineup_score ?? 0, max: 15 },
    { label: "Recent Form", val: breakdown.recent_form ?? breakdown.recent_form_score_v2 ?? 0, max: 15 },
    { label: "Matchup", val: breakdown.matchup_edge ?? breakdown.matchup_score ?? 0, max: 15 },
    { label: "Risk/Data Quality", val: breakdown.risk_adjustment ?? breakdown.data_quality_score ?? 0, max: 10 },
  ] : [];

  return (
    <div className="container py-8 md:py-10 max-w-4xl">
      <Link to="/top-5"><Button variant="ghost" size="sm" className="mb-6"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>

      {/* Hero */}
      <div className="te-card p-6 md:p-7 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-primary opacity-10 blur-3xl pointer-events-none" />
        <div className="flex flex-wrap gap-2 relative">
          <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
          <span className="te-chip border border-border bg-secondary">{BET_LABEL[pick.bet_type]}</span>
          <span className={`te-chip border ${RISK_COLOR[pick.risk]}`}>{pick.risk} risk</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight">{pick.title}</h1>
        {pick.teams && <p className="text-muted-foreground mt-1">{pick.teams}</p>}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mt-6">
          <Stat label="Odds" value={formatOdds(pick.odds)} />
          <Stat label="Units" value={`${pick.unit_rating}u`} accent />
          <Stat label="Confidence" value={`${pick.confidence_score}/100`} />
        </div>
        {(() => {
          const conf = Number(pick.confidence_score ?? 0);
          const edgePct = conf > 50 ? Math.max(0, (conf - 50) / 10) : 0;
          const grade = deriveSignalGrade(conf, edgePct);
          return (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className={`te-chip border text-[10px] tracking-wider font-bold ${grade.confidence.tone}`}>
                {grade.confidence.label}
              </span>
              <span className={`te-chip border text-[10px] tracking-wider font-bold ${grade.marketSignal.tone}`}>
                {grade.marketSignal.label}
              </span>
              <span className="ml-auto"><EdgeExplainer label="How is Edge calculated?" /></span>
            </div>
          );
        })()}
      </div>

      {signals.length > 0 && (
        <div className="te-card p-6 md:p-7 mt-5 md:mt-6">
          <h2 className="font-display text-xl md:text-2xl font-bold flex items-center gap-2"><Activity className="text-primary" /> Why This Pick Scored</h2>
          <div className="mt-5 space-y-3">
            {signals.map((s) => (
              <div key={s.label}>
                <div className="flex justify-between text-sm mb-1.5"><span>{s.label}</span><span className="text-muted-foreground">{s.val}/{s.max}</span></div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-gradient-primary transition-all duration-500" style={{ width: `${(s.val / s.max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pick.ai_reasoning && (
        <div className="te-card p-6 md:p-7 mt-5 md:mt-6">
          <h2 className="font-display text-xl md:text-2xl font-bold">Why this pick</h2>
          <p className="text-muted-foreground mt-3 whitespace-pre-line leading-relaxed">{pick.ai_reasoning}</p>
        </div>
      )}

      {pick.risk_factors && (
        <div className="te-card p-6 md:p-7 mt-5 md:mt-6 border-warning/30">
          <h2 className="font-display text-xl md:text-2xl font-bold flex items-center gap-2"><AlertTriangle className="text-warning" /> Risk factors</h2>
          <p className="text-muted-foreground mt-3">{pick.risk_factors}</p>
        </div>
      )}

      {pick.sportsbook && (
        <div className="te-card p-4 md:p-5 mt-5 md:mt-6 flex items-center gap-2 text-sm flex-wrap">
          <TrendingUp className="w-4 h-4 text-primary" />Source line: <span className="font-semibold">{pick.sportsbook}</span>
          {pick.line != null && <span className="text-muted-foreground">· line {pick.line}</span>}
        </div>
      )}

      <p className="mt-5 text-xs text-muted-foreground text-center">
        Confidence reflects market data + analyst review. Not a guarantee of outcome.
      </p>

      <div className="mt-8"><Disclaimer /></div>
    </div>
  );
}

function Stat({ label, value, accent, tone }: { label: string; value: string; accent?: boolean; tone?: "success" | "danger" }) {
  const toneClass = tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : accent ? "te-grad-text" : "";
  return <div className="rounded-xl bg-secondary/60 px-3 py-3 border border-border/50 text-center"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-display font-bold text-2xl mt-0.5 ${toneClass}`}>{value}</div></div>;
}
